import type { DialogStore } from '@ariakit/react'
import type { FC, ReactElement } from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogDisclosure,
	DialogDismiss,
	DialogFooter,
	DialogHeader,
	DialogHeading,
	DialogProvider
} from '@/components/ui/dialog-atoms'
import { Spinner } from '@/components/ui/spinner'
import { useAuthStore } from '@/features/auth/store/store'
import { errorHandler } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import { useStoreExperienceSubmission } from '../queries/use-store-experience-submission'
import { ShellStepKeyName } from '../types'
import { stepToIcon, stepToLabel } from '../utils'
import { Progress } from './progress'

export const SubmitModal: FC<{
	trigger: ReactElement
	store: DialogStore
	experienceId: number
	galleryImagesCount: number
	isUpgrade: boolean
}> = ({ trigger, store, experienceId, galleryImagesCount, isUpgrade }) => {
	const { t } = useTranslation()
	const user = useAuthStore((state) => state.user)
	const storeExperienceSubmissionMutation = useStoreExperienceSubmission({
		experienceId,
		options: { onError: (error) => errorHandler(error) }
	})
	const stepsToShow = ShellStepKeyName.options.filter(
		(step) => step !== 'settings'
	)
	return (
		<DialogProvider store={store}>
			<DialogDisclosure render={trigger} />
			<Dialog
				className='grid grid-rows-[auto_minmax(0,1fr)_auto]'
				hideOnEscape={!storeExperienceSubmissionMutation.isPending}
				hideOnInteractOutside={
					!storeExperienceSubmissionMutation.isPending
				}
				style={{ width: '680px' }}
			>
				<DialogHeader>
					<DialogDismiss
						disabled={storeExperienceSubmissionMutation.isPending}
					/>
					<DialogHeading>
						{t('experience_shell:submit_to_host')}
					</DialogHeading>
				</DialogHeader>
				<DialogBody>
					<div className='flex flex-col gap-2'>
						<h2 className='font-medium text-2xl'>
							{t('experience_shell:submit_to_host')}
						</h2>
						<DialogDescription>
							{t('experience_shell:submit_to_host_description')}
						</DialogDescription>
					</div>
					<div className='flex w-full flex-col'>
						{stepsToShow.map((step) => {
							const StepIcon = stepToIcon[step]

							return (
								<div
									className='flex w-full items-center justify-between px-2 py-4 [&:not(:last-child)]:border-neutral-300 [&:not(:last-child)]:border-b'
									key={step}
								>
									<div className='flex items-center gap-2'>
										<StepIcon
											className='fill-neutral-600'
											size={24}
										/>
										<h3 className='font-medium underline'>
											{t(stepToLabel[step])}
										</h3>
									</div>
									<Progress
										experienceId={experienceId}
										galleryImagesCount={galleryImagesCount}
										isItineraryLocked={false}
										isLegalLocked={false}
										isUpgrade={isUpgrade}
										step={step}
									/>
								</div>
							)
						})}
					</div>
				</DialogBody>
				<DialogFooter>
					<DialogDismiss
						disabled={storeExperienceSubmissionMutation.isPending}
						render={
							<ButtonLegacy
								size='sm'
								type='button'
								variant='tertiary'
							/>
						}
					>
						{t('common:cancel')}
					</DialogDismiss>
					<ButtonLegacy
						disabled={
							storeExperienceSubmissionMutation.isPending ||
							(user?.role !== 'publisher' &&
								user?.role !== 'admin')
						}
						onClick={() =>
							storeExperienceSubmissionMutation.mutate()
						}
						size='sm'
						type='button'
					>
						{t('experience_shell:submit_to_host')}
						{storeExperienceSubmissionMutation.isPending && (
							<Spinner />
						)}
					</ButtonLegacy>
				</DialogFooter>
			</Dialog>
		</DialogProvider>
	)
}
